import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  QrCode, Download, Printer, Search, RefreshCw,
  ArrowLeft, Grid, List, Check
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const QRManager = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedProducts, setSelectedProducts] = useState([]);

  const isAdmin = user?.role === 'admin' || user?.role === 'master_admin';

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/products`);
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAllQR = async () => {
    if (!isAdmin) {
      toast.error('Necesitas permisos de administrador');
      return;
    }

    setGenerating(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API}/api/admin/products/generate-all-qr`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchProducts(); // Refresh to get updated QR codes
      } else {
        toast.error('Error generando QR codes');
      }
    } catch (err) {
      toast.error('Error de conexión');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadQR = async (product) => {
    try {
      const response = await fetch(`${API}/api/products/${product.product_id}/qr`);
      if (response.ok) {
        const data = await response.json();
        
        const link = document.createElement('a');
        link.download = `QR_${data.sku}_${product.name.replace(/\s+/g, '_')}.png`;
        link.href = data.qr_code;
        link.click();
        toast.success('QR descargado');
      }
    } catch (err) {
      toast.error('Error descargando QR');
    }
  };

  const handlePrintLabel = async (product) => {
    try {
      const response = await fetch(`${API}/api/products/${product.product_id}/qr`);
      if (!response.ok) return;
      const data = await response.json();

      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Etiqueta - ${product.name}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: Arial, sans-serif; 
              display: flex; 
              justify-content: center; 
              align-items: center; 
              min-height: 100vh;
              background: #f5f5f4;
            }
            .label {
              width: 280px;
              background: white;
              border: 2px solid #e7e5e4;
              border-radius: 12px;
              padding: 20px;
              text-align: center;
            }
            .logo { 
              font-size: 14px; 
              font-weight: bold; 
              color: #059669; 
              margin-bottom: 10px;
            }
            .qr { 
              width: 180px; 
              height: 180px; 
              margin: 0 auto 15px;
            }
            .product-name { 
              font-size: 16px; 
              font-weight: bold; 
              color: #292524; 
              margin-bottom: 8px;
              line-height: 1.2;
            }
            .price { 
              font-size: 24px; 
              font-weight: bold; 
              color: #059669; 
              margin-bottom: 10px;
            }
            .sku { 
              font-size: 12px; 
              color: #78716c; 
              font-family: monospace;
              background: #f5f5f4;
              padding: 4px 8px;
              border-radius: 4px;
              display: inline-block;
            }
            .clip-info {
              margin-top: 12px;
              padding-top: 12px;
              border-top: 1px dashed #d6d3d1;
              font-size: 10px;
              color: #a8a29e;
            }
            @media print {
              body { background: white; }
              .label { border: 1px solid #ccc; }
            }
          </style>
        </head>
        <body>
          <div class="label">
            <div class="logo">Lunch & Roll</div>
            <img src="${data.qr_code}" class="qr" alt="QR Code" />
            <div class="product-name">${product.name}</div>
            <div class="price">$${product.price.toFixed(2)} MXN</div>
            <div class="sku">SKU: ${data.sku}</div>
            <div class="clip-info">
              Escanea con tu app o ingresa el SKU en Clip
            </div>
          </div>
          <script>
            window.onload = function() {
              window.print();
            }
          </script>
        </body>
        </html>
      `);
      printWindow.document.close();
    } catch (err) {
      toast.error('Error imprimiendo etiqueta');
    }
  };

  const handlePrintAllSelected = async () => {
    if (selectedProducts.length === 0) {
      toast.error('Selecciona al menos un producto');
      return;
    }

    // Get all QR codes for selected products
    const qrData = await Promise.all(
      selectedProducts.map(async (productId) => {
        const response = await fetch(`${API}/api/products/${productId}/qr`);
        const data = await response.json();
        const product = products.find(p => p.product_id === productId);
        return { ...data, ...product };
      })
    );

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Etiquetas Lunch & Roll</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: Arial, sans-serif; 
            background: white;
            padding: 20px;
          }
          .labels-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 20px;
          }
          .label {
            width: 100%;
            max-width: 250px;
            background: white;
            border: 1px solid #e7e5e4;
            border-radius: 8px;
            padding: 15px;
            text-align: center;
            page-break-inside: avoid;
          }
          .logo { 
            font-size: 12px; 
            font-weight: bold; 
            color: #059669; 
            margin-bottom: 8px;
          }
          .qr { 
            width: 120px; 
            height: 120px; 
            margin: 0 auto 10px;
          }
          .product-name { 
            font-size: 12px; 
            font-weight: bold; 
            color: #292524; 
            margin-bottom: 5px;
            line-height: 1.2;
          }
          .price { 
            font-size: 18px; 
            font-weight: bold; 
            color: #059669; 
            margin-bottom: 5px;
          }
          .sku { 
            font-size: 10px; 
            color: #78716c; 
            font-family: monospace;
          }
          @media print {
            .labels-grid {
              grid-template-columns: repeat(3, 1fr);
            }
          }
        </style>
      </head>
      <body>
        <div class="labels-grid">
          ${qrData.map(item => `
            <div class="label">
              <div class="logo">Lunch & Roll</div>
              <img src="${item.qr_code}" class="qr" alt="QR" />
              <div class="product-name">${item.name}</div>
              <div class="price">$${item.price?.toFixed(2) || '0.00'} MXN</div>
              <div class="sku">SKU: ${item.sku}</div>
            </div>
          `).join('')}
        </div>
        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const selectAll = () => {
    setSelectedProducts(filteredProducts.map(p => p.product_id));
  };

  const deselectAll = () => {
    setSelectedProducts([]);
  };

  const filteredProducts = products.filter(p => 
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.product_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <QrCode className="w-16 h-16 text-stone-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-stone-700">Acceso Restringido</h2>
          <p className="text-stone-500">Necesitas permisos de administrador</p>
          <Link to="/login">
            <Button className="mt-4">Iniciar Sesión</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/admin-dashboard" className="p-2 hover:bg-stone-200 rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-3">
              <QrCode className="w-7 h-7 text-emerald-600" />
              Gestor de QR Codes
            </h1>
            <p className="text-stone-500">Genera, descarga e imprime etiquetas con QR para productos</p>
          </div>
        </div>

        {/* Actions Bar */}
        <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-4 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-stone-400" />
                <input
                  type="text"
                  placeholder="Buscar producto..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              {/* View Toggle */}
              <div className="flex border border-stone-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 ${viewMode === 'grid' ? 'bg-emerald-50 text-emerald-600' : 'bg-white text-stone-400'}`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 ${viewMode === 'list' ? 'bg-emerald-50 text-emerald-600' : 'bg-white text-stone-400'}`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-sm text-stone-500">
                {selectedProducts.length} seleccionados
              </span>
              <Button variant="outline" size="sm" onClick={selectAll}>
                Seleccionar todo
              </Button>
              <Button variant="outline" size="sm" onClick={deselectAll}>
                Deseleccionar
              </Button>
              <Button 
                variant="outline" 
                onClick={handleGenerateAllQR}
                disabled={generating}
                className="gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
                Regenerar QRs
              </Button>
              <Button 
                onClick={handlePrintAllSelected}
                disabled={selectedProducts.length === 0}
                className="gap-2 bg-emerald-600 hover:bg-emerald-700"
              >
                <Printer className="w-4 h-4" />
                Imprimir Seleccionados
              </Button>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <RefreshCw className="w-8 h-8 text-emerald-600 animate-spin" />
          </div>
        ) : (
          <div className={viewMode === 'grid' 
            ? "grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4"
            : "space-y-2"
          }>
            {filteredProducts.map(product => {
              const isSelected = selectedProducts.includes(product.product_id);
              
              return viewMode === 'grid' ? (
                <div 
                  key={product.product_id}
                  className={`bg-white rounded-xl border-2 overflow-hidden cursor-pointer transition-all ${
                    isSelected ? 'border-emerald-500 ring-2 ring-emerald-200' : 'border-stone-200 hover:border-stone-300'
                  }`}
                  onClick={() => toggleProductSelection(product.product_id)}
                >
                  <div className="aspect-square bg-stone-100 relative">
                    {product.image_url ? (
                      <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-3xl">🍽️</div>
                    )}
                    {isSelected && (
                      <div className="absolute top-2 right-2 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                    {product.qr_code && (
                      <div className="absolute bottom-2 left-2 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow">
                        <QrCode className="w-3 h-3 text-emerald-600" />
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="font-medium text-stone-800 text-sm line-clamp-2">{product.name}</p>
                    <p className="text-emerald-600 font-semibold">${product.price?.toFixed(2)}</p>
                    <div className="flex gap-1 mt-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDownloadQR(product); }}
                        className="flex-1 p-1.5 bg-stone-100 rounded-lg hover:bg-stone-200 transition-colors"
                        title="Descargar QR"
                      >
                        <Download className="w-3 h-3 mx-auto text-stone-600" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handlePrintLabel(product); }}
                        className="flex-1 p-1.5 bg-emerald-100 rounded-lg hover:bg-emerald-200 transition-colors"
                        title="Imprimir Etiqueta"
                      >
                        <Printer className="w-3 h-3 mx-auto text-emerald-600" />
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div 
                  key={product.product_id}
                  className={`bg-white rounded-xl border-2 p-4 flex items-center gap-4 cursor-pointer transition-all ${
                    isSelected ? 'border-emerald-500 ring-2 ring-emerald-200' : 'border-stone-200 hover:border-stone-300'
                  }`}
                  onClick={() => toggleProductSelection(product.product_id)}
                >
                  <div className="w-16 h-16 bg-stone-100 rounded-lg overflow-hidden flex-shrink-0">
                    {product.image_url ? (
                      <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-2xl">🍽️</div>
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-stone-800">{product.name}</p>
                    <p className="text-emerald-600 font-semibold">${product.price?.toFixed(2)}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDownloadQR(product); }}
                      className="p-2 bg-stone-100 rounded-lg hover:bg-stone-200"
                    >
                      <Download className="w-4 h-4 text-stone-600" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handlePrintLabel(product); }}
                      className="p-2 bg-emerald-100 rounded-lg hover:bg-emerald-200"
                    >
                      <Printer className="w-4 h-4 text-emerald-600" />
                    </button>
                  </div>
                  {isSelected && (
                    <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Stats */}
        <div className="mt-8 text-center text-stone-500 text-sm">
          {filteredProducts.length} productos • {filteredProducts.filter(p => p.qr_code).length} con QR generado
        </div>
      </div>
    </div>
  );
};

export default QRManager;
